import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

/**
 * Platform-wide configurable theme (primary color, font, favicon…).
 * - On mount, fetches /api/admin/platform-config (every authenticated user can
 *   read) and injects values as CSS variables on :root.
 * - Tailwind's `periwinkle` palette reads `--kc-primary*`, so a super_admin
 *   editing the config re-skins every button, focus ring and glow across the
 *   entire app without a rebuild.
 * - Also updates <link rel="icon"> and document font-family live.
 *
 * Font options (advertised to the admin form):
 */
export const FONT_OPTIONS = [
    { family: "Raleway", label: "Raleway (por defecto)" },
    { family: "Inter", label: "Inter" },
    { family: "DM Sans", label: "DM Sans" },
    { family: "Space Grotesk", label: "Space Grotesk" },
    { family: "Instrument Sans", label: "Instrument Sans" },
    { family: "Instrument Serif", label: "Instrument Serif (display)" },
    { family: "JetBrains Mono", label: "JetBrains Mono (monospace)" },
];

const DEFAULTS = {
    primary_color: "#97ea3f",
    primary_hover_color: "#7cc034",
    primary_text_color: "#000000",
    secondary_color: "#f3f3f3",
    secondary_text_color: "#000000",
    font_family: "Raleway",
    favicon_url: "/krea-logo.png",
    logo_url: "/krea-logo.png",
    platform_name: "Krea.AI",
};

const PlatformConfigContext = createContext({
    config: DEFAULTS,
    refresh: () => {},
    save: async () => {},
});

/** Parse #RRGGBB → "r g b" string (Tailwind's rgb(var(--x) / <alpha>) format). */
function hexToRgbTriplet(hex) {
    if (!hex || typeof hex !== "string") return null;
    const m = hex.trim().match(/^#?([0-9a-f]{6})$/i);
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

function applyToRoot(cfg) {
    const root = document.documentElement;
    const set = (name, value) => value && root.style.setProperty(name, value);
    set("--kc-primary", hexToRgbTriplet(cfg.primary_color));
    set("--kc-primary-hover", hexToRgbTriplet(cfg.primary_hover_color));
    set("--kc-primary-text", hexToRgbTriplet(cfg.primary_text_color));
    set("--kc-secondary", hexToRgbTriplet(cfg.secondary_color));
    set("--kc-secondary-text", hexToRgbTriplet(cfg.secondary_text_color));
    if (cfg.font_family)
        set("--kc-font", `"${cfg.font_family}", Helvetica, Arial, Lucida, sans-serif`);
    if (cfg.favicon_url) {
        let link = document.querySelector("link[rel=icon]");
        if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
        }
        link.type = "image/png";
        link.href = cfg.favicon_url;
    }
    if (cfg.platform_name) {
        document.title = `${cfg.platform_name} · Estudio`;
    }
}

export function PlatformConfigProvider({ children }) {
    const [config, setConfig] = useState(DEFAULTS);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/admin/platform-config");
            setConfig({ ...DEFAULTS, ...r.data });
            applyToRoot({ ...DEFAULTS, ...r.data });
        } catch {
            // unauthenticated — apply defaults so login screen still looks right
            applyToRoot(DEFAULTS);
        }
    }, []);

    const save = useCallback(async (patch) => {
        const r = await api.put("/admin/platform-config", patch);
        setConfig({ ...DEFAULTS, ...r.data });
        applyToRoot({ ...DEFAULTS, ...r.data });
        return r.data;
    }, []);

    useEffect(() => {
        refresh();
        // apply defaults immediately so the screen doesn't flash wrong colors
        applyToRoot(DEFAULTS);
    }, [refresh]);

    return (
        <PlatformConfigContext.Provider value={{ config, refresh, save }}>
            {children}
        </PlatformConfigContext.Provider>
    );
}

export const usePlatformConfig = () => useContext(PlatformConfigContext);
